import React, { useEffect, useMemo, useState } from 'react';
import {
  TextField,
  FormGroup,
  Grid,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';

import styleGlobal from '../../Global.module.css';
import GroupButton from 'components/Buttons/ButtonGroup';
import { useStyles } from '../../components/Styles';
import InfoIcon from '@mui/icons-material/Info';
import { withStyles } from '@mui/styles';
import UnitOfMeasurement from 'components/UnitOfMeasurement';
import api from 'services/api';
import Success from '../../components/Snack/Success';
import Error from '../../components/Snack/Error';
import { useDispatch, useSelector } from 'react-redux';
import { setDisable } from '../../redux/disable';
import { setLoading } from '../../redux/loading';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import NumberFormat from 'react-number-format';
import { removeNullAttributes } from './helpers';
import { formatNumberPtbr } from 'utils/format-number-ptbr';
import { labelField } from 'utils';

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#FFFFFF',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 300,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #0367bf',
  },
}))(Tooltip);

function CustomUnity() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const success = Success();
  const error = Error();
  const disable = useSelector((state) => state.disable.disable);
  const [unities, setUnities] = useState([]);
  const location = useLocation();
  const { id } = useParams();

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get('/unities');
        setUnities(data);
      } catch (e) {
        const errorMessage = e.response?.data?.message;
        throw new Error(errorMessage);
      }
    }
    load();
  }, []);

  async function request(data) {
    dispatch(setLoading(true));
    dispatch(setDisable(true));

    const { baseUnit } = data;

    const newData = {
      ...data,
      baseUnit: removeNullAttributes(baseUnit),
      sigla: baseUnit.sigla,
    };

    try {
      if (id) {
        await api.put(`/unities/${id}`, { ...newData });
      } else {
        await api.post('/unities', { ...newData });
      }

      success();
      history.push('/unities');
    } catch (err) {
      error();
    }
    dispatch(setDisable(false));
    dispatch(setLoading(false));
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      multiplier: '',
      baseUnit: null,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .max(100, 'O tamanho máximo do campo é 100 caracteres')
        .required('Campo Obrigatório'),
      baseUnit: Yup.object().required('Campo Obrigatório').nullable(),
      multiplier: Yup.string()
        .required('Campo Obrigatório')
        .min(1, 'Precisa ser acima de 1')
        .max(99999, 'Limite ultrapassado (5 números).')
        .typeError(
          'O valor deve ser um número. Caso seja um número decimal utilize " . " ao invés de " , "',
        ),
    }),
    onSubmit: (values) => {
      request(values);
    },
  });

  useEffect(() => {
    if (!location.state) return;
    const { data, customData } = location.state;
    const initial = {
      name: (data ? data.name : customData.name) || '',
      multiplier: (data ? Number(data.multiplier) : Number(customData.multiplier)) || '',
      baseUnit: (data ? data.baseUnit : customData.baseUnit) || null,
    };
    if (
      !formik.values.name
      && !formik.values.multiplier
      && !formik.values.baseUnit
    ) {
      formik.setValues(initial);
    }
  }, [formik, location.state]);

  const displayQuantityAbbreviation = useMemo(() => {
    const { multiplier, baseUnit } = formik.values;
    return multiplier && baseUnit ? `${formatNumberPtbr(multiplier)} (${baseUnit.sigla})` : '';
  }, [formik.values]);

  return (
    <header className={styleGlobal.headerGlobal}>
      {/* {openProgress && <Progress />} */}
      <Card style={{ padding: '2rem', borderRadius: '20px' }}>
        <form onSubmit={formik.handleSubmit}>
          <div className={classes.root}>
            <h2 style={{ marginBottom: '2rem' }}>
              Dados de Medida Personalizada
              {' '}
              <HtmlTooltip
                title={(
                  <>
                    Essa tela você consegue cadastrar as unidades de medidas
                    personalizadas que serão utilizadas no sistema.
                  </>
                )}
                placement="right"
                arrow
                interactive
              >
                <InfoIcon color="primary" fontSize="inherit" />
              </HtmlTooltip>
            </h2>

            <CardContent style={{ padding: '0rem' }}>
              <FormGroup>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3} lg={6}>
                    <FormGroup>
                      <TextField
                        inputProps={{ maxLength: 101 }}
                        InputLabelProps={{ style: { pointerEvents: 'auto' } }}
                        id="name"
                        label={labelField('Nome')}
                        variant="outlined"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={!!(formik.touched.name && formik.errors.name)}
                        helperText={
                          formik.touched.name
                          && formik.errors.name
                          && formik.errors.name
                        }
                      />
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} md={3} lg={2}>
                    <FormGroup>
                      <NumberFormat
                        inputProps={{ maxLength: 7 }}
                        InputLabelProps={{ style: { pointerEvents: 'auto' } }}
                        id="multiplier"
                        fullWidth
                        label={labelField('Quantidade')}
                        variant="outlined"
                        name="multiplier"
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        value={formik.values.multiplier}
                        onValueChange={(values) => {
                          formik.setValues({
                            ...formik.values,
                            multiplier: values.floatValue,
                          });
                        }}
                        onBlur={formik.handleBlur}
                        customInput={TextField}
                        error={
                          !!(
                            formik.touched.multiplier
                            && formik.errors.multiplier
                          )
                        }
                        helperText={
                          formik.touched.multiplier && formik.errors.multiplier
                        }
                      />
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} md={3} lg={2}>
                    <UnitOfMeasurement
                      selectedUnity={formik.values.baseUnit}
                      autoCompleteOptions={unities}
                      onBlur={() => formik.setFieldTouched('baseUnit', true)}
                      onChange={(event, newValue) => {
                        formik.setFieldValue('baseUnit', newValue);
                      }}
                      error={
                        !!(
                          formik.touched.baseUnit
                          && formik.errors.baseUnit
                          && !formik.values.baseUnit
                        )
                      }
                      helperText={
                        formik.touched.baseUnit
                        && formik.errors.baseUnit
                        && formik.errors.baseUnit
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3} lg={2}>
                    <FormGroup>
                      <TextField
                        disabled
                        InputLabelProps={{ style: { pointerEvents: 'auto' } }}
                        label="Qtde + Símbolo/Sigla"
                        variant="outlined"
                        value={displayQuantityAbbreviation}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </FormGroup>
            </CardContent>
          </div>

          <GroupButton
            deleteRoute="/unities/"
            routeDelete="/unities"
            returnRoute="/unities"
            id={id}
            disable={disable}
            message="A exclusão dessa unidade de medida pode levar a exclusão de objetos que dependam dela. Tem certeza que deseja excluir?"
          />
        </form>
      </Card>
    </header>
  );
}

export default CustomUnity;
